import { useNavigation } from '@react-navigation/native'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { MyCoaches } from 'partials/Coaches/MyCoaches'

import { Bio } from 'widgets/Bio'
import { Box } from 'widgets/Box'
import { Button } from 'widgets/Button'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Text } from 'widgets/Text'
import { UpscoreBadge } from 'widgets/UpscoreBadge'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDMRoom } from 'hooks/useDMRoom'
import { useFollow } from 'hooks/useFollow'
import { useFollows } from 'hooks/useFollows'
import { useFriends } from 'hooks/useFriends'

import { links } from 'utils/constants'
import { defaultPostThumbnailUrl } from 'utils/media'
import { createPartialUser } from 'utils/user/createPartialUser'

import { UserType } from 'types/user'

import { ConnectionCount } from './ConnectionCount'

import { UserCoaches } from '../Coaches/UserCoaches'

const styles = StyleSheet.create({
  innerUserContainer: {
    borderRadius: 65,
  },
  image: {
    flex: 1,
    borderRadius: 65,
  },
  firstButton: {
    marginRight: 8,
  },
  upscoreContainer: {
    position: 'absolute',
    top: -6,
    right: -6,
    zIndex: 2,
    paddingLeft: 2,
  },
})

export const ProfileHeader = ({ user }: { user: UserType }) => {
  const navigation = useNavigation()
  const { data: currentUser } = useCurrentUser()
  const isCurrentUser = useMemo(() => currentUser?.id === user?.id, [currentUser?.id, user?.id])

  const navigateToFollowersPage = () => {
    navigation.navigate(links.FOLLOWERS_FRIENDS_PAGE, {
      tab: 'followers',
      userId: user.id,
    })
  }

  const navigateToFriendsPage = () => {
    navigation.navigate(links.FOLLOWERS_FRIENDS_PAGE, {
      tab: 'friends',
      userId: user.id,
    })
  }

  const navigateToUpscorePage = () => {
    navigation.navigate(links.UPSCORE_PAGE, {
      userId: user.id,
    })
  }

  const {
    follow,
    unfollow,
    data: following,
    isLoading: isLoadingFollow,
  } = useFollow({
    trainer: user,
  })
  const { data: userFollows, isLoading: isLoadingUserFollows } = useFollows(user?.id)
  const userFollowsYou = userFollows?.find(
    (userFollow) => userFollow.trainer.id === currentUser?.id,
  )
  const { data: friends, isLoading: isLoadingFriends, refetch } = useFriends(currentUser?.id)
  const isLoading = isLoadingFollow && isLoadingUserFollows && isLoadingFriends
  const [isFriends, setIsFriends] = useState(friends?.find((friend) => friend.id === user?.id))
  useEffect(() => {
    setIsFriends(friends?.find((friend) => friend.id === user?.id))
  }, [friends, isLoadingFriends, user?.id, user?.friendCount])

  const handleFollowButtonPress = () => {
    setIsFirstButtonLoading(true)
    if (isFriends || following) {
      unfollow.mutateAsync({ followId: following?.id })
    } else if (!isFriends && !following) {
      follow.mutateAsync()
    }
  }

  const [isFirstButtonLoading, setIsFirstButtonLoading] = useState(true)
  const [relationshipText, setRelationshipText] = useState('')
  useEffect(() => {
    if (!isCurrentUser) {
      if (isFriends) {
        setRelationshipText('Friends')
      } else if (following) {
        setRelationshipText('Following')
      } else if (userFollowsYou) {
        setRelationshipText('Follow Back')
      } else {
        setRelationshipText('Follow')
      }
    }

    if (!isLoading) {
      setIsFirstButtonLoading(false)
    }
  }, [
    currentUser?.id,
    following,
    isCurrentUser,
    isFriends,
    userFollows,
    isLoading,
    userFollowsYou,
    refetch,
  ])

  const { createRoom } = useDMRoom({})
  const createChatRoom = useCallback(async () => {
    if (currentUser && user) {
      const { id: roomId } = await createRoom.mutateAsync({
        participants: [createPartialUser(currentUser), createPartialUser(user)],
      })

      navigation.navigate(links.CHAT_ROOM, { roomId })
    }
  }, [currentUser, user, navigation, createRoom])

  return (
    <>
      <Box
        backgroundColor='white'
        flexDirection='column'
        align='center'
        justifyContent='flex-end'
        margin={{ top: 8 }}
      >
        <Box
          width='100%'
          flexDirection='column'
          align='center'
          justifyContent='center'
          padding={{ horizontal: 'md', bottom: 'md' }}
        >
          <Box
            testID='AvatarRow'
            width='100%'
            flexDirection='row'
            align='center'
            justifyContent='space-between'
            padding={{ horizontal: 'md' }}
          >
            <ConnectionCount
              onPress={navigateToFollowersPage}
              count={user?.followerCount ?? 0}
              connection='Followers'
            />
            <Box
              testID='UserAvatarContainer'
              height={104}
              width={104}
              padding='xs'
              margin={{ bottom: 'md' }}
              border={{ color: 'v2primary', width: 2 }}
              style={styles.innerUserContainer}
            >
              <Image
                cacheKey={
                  // eslint-disable-next-line no-underscore-dangle
                  user ? `${user.id}-${user.avatar?._original?.etag}-avatar` : uuidv4()
                }
                imageWidth={104}
                style={styles.image}
                source={{
                  uri: user.avatar?.url || defaultPostThumbnailUrl,
                }}
              />
              <Box testID='UpscoreContainer' style={styles.upscoreContainer}>
                <UpscoreBadge
                  text={`${user.upscore?.total ?? 0}`}
                  onPress={navigateToUpscorePage}
                />
              </Box>
            </Box>
            <ConnectionCount
              onPress={navigateToFriendsPage}
              count={user?.friendCount ?? 0}
              connection='Friends'
            />
          </Box>

          <Box testID='ButtonRow' flexDirection='row' align='center' margin={{ bottom: 'md' }}>
            <Button
              testID='FirstUserButton'
              isLoading={isFirstButtonLoading}
              type={!isCurrentUser && !following ? 'v2primary' : 'greyLight'}
              borderRadius='sm'
              padding={{ vertical: !isFirstButtonLoading ? 'sm' : 10, horizontal: 'md' }}
              style={!isCurrentUser ? styles.firstButton : undefined}
              onPress={
                isCurrentUser
                  ? () => navigation.navigate(links.EDIT_PROFILE_PAGE)
                  : () => handleFollowButtonPress()
              }
            >
              <Text color={!isCurrentUser && !following ? 'white' : 'v2black'}>
                {isCurrentUser ? 'Edit' : relationshipText}
              </Text>
            </Button>
            {!isCurrentUser && (
              <Button type='greyLight' borderRadius='sm' padding='sm' onPress={createChatRoom}>
                <Icon name='message-circle' color='v2black' size={28} />
              </Button>
            )}
          </Box>

          <Box width='100%' flexDirection='row' align='center' justifyContent='space-between'>
            <Box flexDirection='column' align='flex-start' justifyContent='center'>
              <Text fontSize='md' color='v2black' bold>
                @{user?.username}
              </Text>
            </Box>
            <Box flexDirection='column' align='flex-end' justifyContent='center'>
              <Text fontSize='sm' color='greyDark'>
                {user?.address?.city} {user?.address?.state}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {user.bio ? (
        <Box margin={{ horizontal: 'md', bottom: 'sm' }}>
          <Bio bio={user.bio} />
        </Box>
      ) : null}

      {isCurrentUser ? (
        <MyCoaches
          isSquare
          noCoachesHeaderText='Team Up!'
          noCoachesBodyText='Create or join a Group'
          withLeaderboardPosition
        />
      ) : (
        <UserCoaches userId={user?.id} />
      )}
    </>
  )
}
